import React, { useContext, useState } from 'react'
import { useHistory } from 'react-router-dom'

import { Avatar,Div, Span } from '@driftt/tide-core'
import { Icon } from '@driftt/tide-icons'

import ActivityActions, { ActionType } from 'components/ActivityActions'
import Card from 'components/Card'
import {
  driftEmptyStateBuilder,
  driftErrorStateBuilder,
  driftLoadingStateBuilder} from 'components/States'
import { InfiniteScrollTable } from 'components/TableView'
import { SortContext } from 'context/sortContext'
import { useAccountContactActivity } from 'hooks/useAccountContactActivity'
import { CompanyData,ContactWithActivites } from 'modules/accountActivity/domain'
import { AccountActivitySortValue } from 'modules/accountActivity/domain'
import {
  getDisplayStringWithFallback,
  getEndUserDisplayLocation
} from 'utils/endUser'
import { formatNumber } from 'utils/formatNumbers'
import { accountPath, contactPath } from 'utils/paths'
import { humanizedRelativeTime } from 'utils/timeStamp'
import { segmentTrack, TrackType } from 'utils/tracking'

import SortDescription from './SortDescription'

import styles from './styles.module.css'

const contactTableHeaders = [
  {
    label: 'Contact',
    margin: '56px',
    sortable: true,
    sortValue: AccountActivitySortValue.CONTACT_NAME,
    initialDescending: false
  },
  {
    label: 'Role',
    sortable: true,
    sortValue: AccountActivitySortValue.CONTACT_ROLE,
    initialDescending: false
  },
  {
    label: 'Location',
    sortable: true,
    sortValue: AccountActivitySortValue.LOCATION,
    initialDescending: false
  },
  {
    label: '# activities',
    sortable: true,
    sortValue: AccountActivitySortValue.NUM_ACTIVITIES
  },
  {
    label: 'Last active',
    margin: '7px',
    sortable: true,
    sortValue: AccountActivitySortValue.MOST_RECENT
  }
]

interface Props {
  accountId: string
  companyName?: string
}

export const role = (contactActivity: ContactWithActivites) => (
  <Span>{getDisplayStringWithFallback(contactActivity.endUserData.role)}</Span>
)

export const location = (contactActivity: ContactWithActivites) => (
  <Span>
    {getEndUserDisplayLocation(
      contactActivity.endUserData.locationCity,
      contactActivity.endUserData.locationStateCode,
      contactActivity.endUserData.locationCountryCode
    )}
  </Span>
)

export const numberOfActivities = (contactActivity: ContactWithActivites) => (
  <Span>{formatNumber(contactActivity.activityCount)}</Span>
)

export const contactInfo = (contactActivity: ContactWithActivites) => {
  const avatarCompatibleEndUser = { ...contactActivity.endUserData, type: 'END_USER' }
  return (
    <div className={styles.contactInfo}>
      <Avatar
        user={avatarCompatibleEndUser}
        withPresence={false}
        className={styles.avatarContainer}
      />
      {endUserDisplayNameWithEmail(
        contactActivity.endUserData.name,
        contactActivity.endUserData.email
      )}
    </div>
  )
}

export const endUserDisplayNameWithEmail = (displayName?: string, email?: string) => {
  if (
    typeof displayName !== 'undefined' &&
    displayName !== '' &&
    displayName !== null &&
    typeof email !== 'undefined' &&
    email !== null
  ) {
    return (
      <Div className={styles.endUserDisplayNameAndEmail}>
        <Span className={styles.endUserDisplayName}>{displayName}</Span>
        <Span className={styles.endUserDisplayEmail}>{getDisplayStringWithFallback(email)}</Span>
      </Div>
    )
  } else if (typeof email !== 'undefined' && email !== null) {
    return (
      <Div className={styles.endUserDisplayNameAndEmail}>
        <Span className={styles.endUserDisplayName}>{getDisplayStringWithFallback(email)}</Span>
      </Div>
    )
  } else {
    return (
      <Div className={styles.endUserDisplayNameAndEmail}>
        <Span className={styles.endUserDisplayName}>Site visitor</Span>
      </Div>
    )
  }
}

export const lastActive = (companyData?: CompanyData) => (
  contactActivity: ContactWithActivites
) => {
  return (
    <Div className={styles.lastActiveWithActivityActions}>
      <ActivityActions
        className="activity-actions"
        endUserData={contactActivity.endUserData}
        companyData={companyData}
        actionTypes={[
          ActionType.EMAIL,
          ActionType.LINKEDIN,
          ActionType.OUTREACH,
          ActionType.SALESLOFT,
          ActionType.CONTACT_INFO
        ]}
      />
      <Span className="contact-info-last-active">
        {humanizedRelativeTime(contactActivity.timeOfLastEngagement)}
      </Span>
    </Div>
  )
}

const errorComponent = driftErrorStateBuilder('this accounts contacts')
const loadingComponent = driftLoadingStateBuilder('Loading contacts')
const emptyComponent = driftEmptyStateBuilder(
  'No contacts yet',
  'Once a contact visits your site, they will show up here.'
)

const ContactsSection = (props: Props) => {
  const history = useHistory()
  const { accountId, companyName } = props
  const [isOpen, setIsOpen] = useState(true)
  const { contactsSort, contactsDescending, setContactsDescending, setContactsSort } = useContext(
    SortContext
  )

  const {
    response: accountContacts,
    isLoadingAccountContacts,
    accountContactsError
  } = useAccountContactActivity(accountId)

  const handleRowClick = (contact: ContactWithActivites) => {
    segmentTrack(TrackType.ACCOUNT_DETAILS_CONTACT_ROW_CLICK)
    history.push({
      pathname: contactPath(contact.endUserData.id),
      state: {
        navigatedFrom: companyName ?? 'Account details',
        navigatedFromUrl: accountPath(accountId)
      }
    })
  }

  return (
    <Card className={styles.cardContainer}>
      <div className={styles.cardHeader}>
        <h3 className={styles.header}>Contacts</h3>
        <div className={styles.collapseIcon} onClick={() => setIsOpen(!isOpen)}>
          <Icon name={isOpen ? 'chevron-down' : 'chevron-up'} width={12} />
        </div>
      </div>
      {isOpen && (
        <div className={styles.cardContent}>
          <SortDescription
            count={accountContacts?.length || 0}
            sort={contactsSort}
            descending={contactsDescending}
          />
          <div className={styles.contactsTable}>
            <InfiniteScrollTable
              rowClassName={styles.contactRowStructureStyle}
              initialDataLoaded={!isLoadingAccountContacts}
              isLoadingInitialData={isLoadingAccountContacts}
              rowHeight={56}
              columns={contactTableHeaders}
              resultSize={accountContacts?.length || 0}
              data={accountContacts ?? []}
              loadMore={() => {}}
              error={accountContactsError}
              errorComponent={errorComponent}
              loadingComponent={loadingComponent}
              emptyComponent={emptyComponent}
              height={340}
              cellRenderers={[
                contactInfo,
                role,
                location,
                numberOfActivities,
                accountContacts?.[0]?.companyData
                  ? lastActive(accountContacts?.[0]?.companyData)
                  : () => {}
              ]}
              onRowClick={handleRowClick}
              selectedSort={contactsSort}
              onSortByChange={setContactsSort}
              descending={contactsDescending}
              onSortDirChange={setContactsDescending}
            />
          </div>
        </div>
      )}
    </Card>
  )
}

export default ContactsSection
